import { type ReactNode, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import AddIcon from '@material-ui/icons/Add';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubChip,
  DevHubCircularProgress,
  DevHubDialog,
  DevHubDialogActions,
  DevHubDialogContent,
  DevHubDialogTitle,
  DevHubDivider,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import type { ContextEntry, ContextType, FlowPool } from './types';
import {
  type EntryCredentialsState,
  EMPTY_ENTRY_CREDENTIALS,
  EntryCredentialsFields,
} from './EntryCredentialsFields';
import { CONTEXT_TYPE_LABELS, computeVaultEntryKey, entryKey } from './utils';

/**
 * Returns true if the given string is a well-formed absolute URL.
 * @param value The string to validate
 */
const isValidUrl = (value: string): boolean => {
  try {
    return !!new URL(value);
  } catch {
    return false;
  }
};

const FIELD_STYLE = { style: { paddingRight: '2rem' } } as const;

interface AddContextDialogProps {
  open: boolean;
  onClose: () => void;
  addType: ContextType;
  channelEntries: ContextEntry[];
  selectedChannel: string | null;
  backendUrl: string;
  onSave: (entries: ContextEntry[]) => Promise<void>;
}

/**
 * AddContextDialog function
 * @param opts options
 * @constructor
 */
export const AddContextDialog = ({
  open,
  onClose,
  addType,
  channelEntries,
  selectedChannel,
  backendUrl,
  onSave,
}: AddContextDialogProps) => {
  const identityApi = useApi(identityApiRef);

  const [addValue, setAddValue] = useState('');
  const [addGithubOrg, setAddGithubOrg] = useState('');
  const [addGithubRepo, setAddGithubRepo] = useState('');
  const [addGithubPath, setAddGithubPath] = useState('');
  const [addFileName, setAddFileName] = useState<string | null>(null);
  const [addFileObj, setAddFileObj] = useState<File | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addLoadingLabel, setAddLoadingLabel] = useState('Saving…');
  const [addAgentAuth] = useState<'sat'>('sat');
  const [addAgentDescription, setAddAgentDescription] = useState('');
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [credExpanded, setCredExpanded] = useState(false);
  const [creds, setCreds] = useState<EntryCredentialsState>(
    EMPTY_ENTRY_CREDENTIALS,
  );

  const reset = () => {
    setAddValue('');
    setAddGithubOrg('');
    setAddGithubRepo('');
    setAddGithubPath('');
    setAddFileName(null);
    setAddFileObj(null);
    setAddError(null);
    setAddLoading(false);
    setAddLoadingLabel('Saving…');
    setRewriteLoading(false);
    setCredExpanded(false);
    setCreds(EMPTY_ENTRY_CREDENTIALS);
  };

  const saveEntryCredentials = async (vaultEntryKey: string) => {
    if (
      !creds.tokenUrl.trim() ||
      !creds.clientId.trim() ||
      !creds.clientSecret.trim()
    )
      return;
    if (!selectedChannel) return;
    try {
      const { token } = await identityApi.getCredentials();
      await fetch(
        `${backendUrl}/api/agent/channel/${selectedChannel}/context-entry-credentials`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            entryKey: vaultEntryKey,
            tokenUrl: creds.tokenUrl.trim(),
            clientId: creds.clientId.trim(),
            clientSecret: creds.clientSecret.trim(),
            scopes: creds.scopes.trim(),
            authType: creds.authType,
          }),
        },
      );
    } catch {
      // Non-blocking — credentials can be set later via the edit button
    }
  };

  const handleRewriteDescription = async () => {
    if (!addValue.trim()) return;
    setRewriteLoading(true);
    try {
      const { token } = await identityApi.getCredentials();

      // Try to fetch the agent card for richer context
      let agentCardContext = '';
      try {
        const agentOrigin = new URL(addValue.trim()).origin;
        const cardRes = await fetch(
          `${agentOrigin}/.well-known/agent-card.json`,
          { signal: AbortSignal.timeout(3000) },
        );
        if (cardRes.ok) {
          const card = await cardRes.json();
          const skills = (card.skills ?? [])
            .map((s: any) => `- ${s.name}: ${s.description}`)
            .join('\n');
          agentCardContext = [
            card.name ? `Agent name: ${card.name}` : '',
            card.description ? `Agent description: ${card.description}` : '',
            skills ? `Skills:\n${skills}` : '',
          ]
            .filter(Boolean)
            .join('\n');
        }
      } catch {
        // agent card unavailable — fall back to URL only
      }

      const prompt = agentCardContext
        ? `Write a one-sentence description telling Nova (an AI orchestrator) when to delegate questions to this agent.\n\n${agentCardContext}\n\nBe concise and specific. Reply with only the description, no extra text.`
        : `Write a one-sentence description for an AI agent available at this A2A endpoint: ${addValue.trim()}. The description should tell Nova (another AI agent) when to delegate questions to this agent. Be concise and specific. Reply with only the description, no extra text.`;

      const res = await fetch(`${backendUrl}/api/agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: prompt }),
      });
      if (!res.ok || !res.body) return;
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let generated = '';
      let done = false;
      while (!done) {
        const result = await reader.read();
        done = result.done;
        if (done) break;
        const chunk = decoder.decode(result.value, { stream: true });
        for (const line of chunk.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed.text) {
              generated += parsed.text;
              setAddAgentDescription(generated);
            }
          } catch {
            // ignore malformed SSE lines
          }
        }
      }
    } catch {
      // silently fail — user can type manually
    } finally {
      setRewriteLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAddFileName(file.name);
    setAddFileObj(file);
    setAddError(null);
    if (
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
    ) {
      setAddValue(file.name);
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => setAddValue((ev.target?.result as string) ?? '');
    reader.onerror = () => setAddError('Failed to read file.');
    reader.readAsText(file);
  };

  const handleAdd = async () => {
    let entry: ContextEntry;
    if (addType === 'agent') {
      if (!addValue.trim()) {
        setAddError('Agent URL is required.');
        return;
      }
      if (!isValidUrl(addValue.trim())) {
        setAddError('Enter a valid URL.');
        return;
      }
      entry = {
        type: 'agent',
        url: addValue.trim(),
        auth: addAgentAuth,
        ...(addAgentDescription.trim()
          ? { description: addAgentDescription.trim() }
          : {}),
      };
      if (channelEntries.some(e => entryKey(e) === entryKey(entry))) {
        setAddError('This agent endpoint is already added to the channel.');
        return;
      }
      setAddLoading(true);
      setAddLoadingLabel('Verifying…');
      setAddError(null);
      try {
        const { token } = await identityApi.getCredentials();
        const verifyRes = await fetch(
          `${backendUrl}/api/agent/verify-agent-endpoint`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              url: addValue.trim(),
              channelId: selectedChannel ?? undefined,
            }),
          },
        );
        const verifyData = await verifyRes.json();
        if (!verifyData.valid) {
          setAddError(verifyData.error ?? 'Endpoint verification failed.');
          return;
        }
        setAddLoadingLabel('Saving…');
        await onSave([...channelEntries, entry]);
        // Save optional per-entry credentials to Vault after the entry is persisted
        if (
          creds.tokenUrl.trim() &&
          creds.clientId.trim() &&
          creds.clientSecret.trim()
        ) {
          const vaultKey = await computeVaultEntryKey({
            type: 'agent',
            url: addValue.trim(),
          });
          await saveEntryCredentials(vaultKey);
        }
        handleClose();
      } catch {
        setAddError('Failed to verify or save context entry.');
      } finally {
        setAddLoading(false);
        setAddLoadingLabel('Saving…');
      }
      return;
    } else if (addType === 'github') {
      if (!addGithubOrg.trim() || !addGithubRepo.trim()) {
        setAddError('Org and repo are required.');
        return;
      }
      entry = {
        type: 'github',
        org: addGithubOrg.trim(),
        repo: addGithubRepo.trim(),
        path: addGithubPath.trim(),
      };
    } else if (addType === 'file') {
      if (!addFileObj) {
        setAddError('Select a file first.');
        return;
      }
      const isPdf =
        addFileObj.type === 'application/pdf' ||
        addFileObj.name.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        if (!selectedChannel || selectedChannel === '__all__') {
          setAddError('No channel selected.');
          return;
        }
        setAddLoading(true);
        setAddError(null);
        try {
          const { token } = await identityApi.getCredentials();
          const formData = new FormData();
          formData.append('file', addFileObj);
          formData.append('platform', 'slack');
          const res = await fetch(
            `${backendUrl}/api/agent/channel/${selectedChannel}/index-file`,
            {
              method: 'POST',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              body: formData,
            },
          );
          if (!res.ok) throw new Error('Upload failed');
          await onSave([
            ...channelEntries,
            { type: 'file', value: addFileObj.name },
          ]);
          handleClose();
        } catch {
          setAddError('Failed to upload PDF.');
        } finally {
          setAddLoading(false);
        }
        return;
      }
      entry = { type: 'text', value: addValue.trim() };
    } else {
      if (!addValue.trim()) {
        setAddError('Value is required.');
        return;
      }
      entry = { type: addType, value: addValue.trim() } as ContextEntry;
    }
    if (channelEntries.some(e => entryKey(e) === entryKey(entry))) {
      setAddError('This entry is already added to the channel.');
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await onSave([...channelEntries, entry]);
      handleClose();
    } catch {
      setAddError('Failed to save context entry.');
    } finally {
      setAddLoading(false);
    }
  };

  let body: ReactNode;
  if (addType === 'agent') {
    body = (
      <DevHubBox display="flex" flexDirection="column" style={{ gap: 12 }}>
        <DevHubTextField
          label="Agent URL"
          fullWidth
          value={addValue}
          onChange={(e: any) => setAddValue(e.target.value)}
          placeholder="https://agent.example.com/a2a"
          {...FIELD_STYLE}
        />
        <DevHubBox>
          <DevHubTextField
            label="Description (optional)"
            fullWidth
            value={addAgentDescription}
            onChange={(e: any) => setAddAgentDescription(e.target.value)}
            placeholder="e.g. Answers questions about PagerDuty incidents for this team"
            helperText="Helps Nova know when to use this agent."
            {...FIELD_STYLE}
          />
          <DevHubBox
            display="flex"
            justifyContent="flex-end"
            style={{ marginTop: 4 }}
          >
            <DevHubButton
              size="small"
              variant="text"
              disabled={!addValue.trim() || rewriteLoading}
              onClick={handleRewriteDescription}
            >
              {rewriteLoading ? (
                <DevHubCircularProgress size={14} style={{ marginRight: 6 }} />
              ) : null}
              {rewriteLoading ? 'Writing…' : '✦ Rewrite with AI'}
            </DevHubButton>
          </DevHubBox>
        </DevHubBox>
        <DevHubTextField
          label="Auth mechanism"
          fullWidth
          value={addAgentAuth.toUpperCase()}
          InputProps={{ readOnly: true }}
          helperText="Only SAT is supported at this time."
          {...FIELD_STYLE}
        />
        <DevHubBox>
          <DevHubButton
            variant="text"
            size="small"
            onClick={() => setCredExpanded(v => !v)}
          >
            {credExpanded
              ? '▾ Hide credentials'
              : '▸ Add custom credentials (optional)'}
          </DevHubButton>
          {credExpanded && (
            <DevHubBox style={{ marginTop: 8 }}>
              <EntryCredentialsFields
                value={creds}
                onChange={setCreds}
                caption="These credentials will be used when Nova calls this agent. If omitted, the channel or server credentials are used."
              />
            </DevHubBox>
          )}
        </DevHubBox>
      </DevHubBox>
    );
  } else if (addType === 'file') {
    body = (
      <DevHubBox display="flex" flexDirection="column" style={{ gap: 12 }}>
        <label style={{ display: 'inline-flex', cursor: 'pointer' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '5px 15px',
              border: '1px solid',
              borderRadius: 4,
              fontSize: 14,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            {addFileName ?? 'Choose file'}
          </span>
          <input
            type="file"
            hidden
            accept=".txt,.md,.json,.yaml,.yml,.pdf,application/pdf"
            onChange={handleFileChange}
          />
        </label>
        {addValue && (
          <DevHubTextField
            label="Preview"
            multiline
            minRows={4}
            fullWidth
            value={addValue.slice(0, 500) + (addValue.length > 500 ? '…' : '')}
            InputProps={{ readOnly: true }}
            {...FIELD_STYLE}
          />
        )}
      </DevHubBox>
    );
  } else if (addType === 'github') {
    body = (
      <DevHubBox display="flex" flexDirection="column" style={{ gap: 12 }}>
        <DevHubTextField
          label="GitHub Org"
          fullWidth
          value={addGithubOrg}
          onChange={(e: any) => setAddGithubOrg(e.target.value)}
          placeholder="myorg"
          {...FIELD_STYLE}
        />
        <DevHubTextField
          label="Repo"
          fullWidth
          value={addGithubRepo}
          onChange={(e: any) => setAddGithubRepo(e.target.value)}
          placeholder="my-service"
          {...FIELD_STYLE}
        />
        <DevHubTextField
          label="Path (optional)"
          fullWidth
          value={addGithubPath}
          onChange={(e: any) => setAddGithubPath(e.target.value)}
          placeholder="docs"
          helperText="Leave empty to index all .md files in the root of the repo."
          {...FIELD_STYLE}
        />
      </DevHubBox>
    );
  } else {
    body = (
      <DevHubTextField
        label={addType === 'url' ? 'URL' : 'Text'}
        multiline={addType === 'text'}
        minRows={addType === 'text' ? 3 : undefined}
        fullWidth
        value={addValue}
        onChange={(e: any) => setAddValue(e.target.value)}
        placeholder={
          addType === 'url'
            ? 'https://wiki.example.com/my-service'
            : 'e.g. On-call rotation: Alice (Mon–Wed), Bob (Thu–Sun)'
        }
        {...FIELD_STYLE}
      />
    );
  }

  return (
    <DevHubDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DevHubDialogTitle>
        Add {CONTEXT_TYPE_LABELS[addType]} Context
      </DevHubDialogTitle>
      <DevHubDialogContent>
        <DevHubDivider style={{ margin: '4px 0 16px 0' }} />
        {body}
        {addError && (
          <DevHubBox mt={2}>
            <DevHubAlert severity="error">{addError}</DevHubAlert>
          </DevHubBox>
        )}
      </DevHubDialogContent>
      <DevHubDialogActions>
        <DevHubButton
          variant="outlined"
          color="secondary"
          height="large"
          onClick={handleClose}
        >
          Cancel
        </DevHubButton>
        <DevHubButton
          variant="contained"
          color="primary"
          height="large"
          onClick={handleAdd}
          disabled={addLoading}
          startIcon={
            addLoading ? (
              <DevHubCircularProgress size={14} />
            ) : (
              <AddIcon fontSize="small" />
            )
          }
        >
          {addLoading ? addLoadingLabel : 'Add'}
        </DevHubButton>
      </DevHubDialogActions>
    </DevHubDialog>
  );
};

export type FlowEntryCredentials = EntryCredentialsState;

interface McpServerPickerDialogProps {
  open: boolean;
  onClose: () => void;
  saving: boolean;
  error: string | null;
  onAdd: (
    url: string,
    label: string | undefined,
    credentials?: EntryCredentialsState,
  ) => void;
}

/**
 * McpServerPickerDialog — adds a direct MCP server as a channel context source.
 * Auth uses Azure AD (with SAT fallback); custom credentials can be stored in Vault.
 * @param root0 Component props
 * @param root0.open Whether the dialog is open
 * @param root0.onClose Callback to close the dialog
 * @param root0.onAdd Callback to add the MCP server
 */
export const McpServerPickerDialog = ({
  open,
  onClose,
  saving,
  error,
  onAdd,
}: McpServerPickerDialogProps) => {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [credExpanded, setCredExpanded] = useState(false);
  const [creds, setCreds] = useState<EntryCredentialsState>(
    EMPTY_ENTRY_CREDENTIALS,
  );
  const [urlError, setUrlError] = useState<string | null>(null);

  const reset = () => {
    setUrl('');
    setLabel('');
    setCredExpanded(false);
    setCreds(EMPTY_ENTRY_CREDENTIALS);
    setUrlError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAdd = () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      setUrlError('MCP server URL is required.');
      return;
    }
    if (!isValidUrl(trimmedUrl)) {
      setUrlError('Enter a valid URL.');
      return;
    }
    setUrlError(null);
    const hasCredentials =
      creds.tokenUrl.trim() &&
      creds.clientId.trim() &&
      creds.clientSecret.trim();
    onAdd(
      trimmedUrl,
      label.trim() || undefined,
      hasCredentials ? creds : undefined,
    );
  };

  return (
    <DevHubDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DevHubDialogTitle>Add MCP Server</DevHubDialogTitle>
      <DevHubDialogContent>
        <DevHubDivider style={{ margin: '4px 0 16px 0' }} />
        <DevHubBox display="flex" flexDirection="column" style={{ gap: 12 }}>
          <DevHubTextField
            label="MCP server URL"
            fullWidth
            value={url}
            onChange={(e: any) => {
              setUrl(e.target.value);
              setUrlError(null);
            }}
            placeholder="https://my-mcp-server.internal.com/mcp"
            error={!!urlError}
            helperText={urlError ?? 'The MCP endpoint Nova will connect to.'}
            {...FIELD_STYLE}
          />
          <DevHubTextField
            label="Label (optional)"
            fullWidth
            value={label}
            onChange={(e: any) => setLabel(e.target.value)}
            placeholder="e.g. Kong Metrics"
            helperText="Short display name shown in the context list."
            {...FIELD_STYLE}
          />
          <DevHubBox>
            <DevHubButton
              variant="text"
              size="small"
              onClick={() => setCredExpanded(v => !v)}
            >
              {credExpanded
                ? '▾ Hide credentials'
                : '▸ Add custom credentials (optional)'}
            </DevHubButton>
            {credExpanded && (
              <DevHubBox style={{ marginTop: 8 }}>
                <EntryCredentialsFields
                  value={creds}
                  onChange={setCreds}
                  caption="SAT credentials stored in Vault. Used when Nova calls this server as a bot (no user session). If omitted, the server-level credentials are used."
                />
              </DevHubBox>
            )}
          </DevHubBox>
        </DevHubBox>
        {error && (
          <DevHubBox mt={2}>
            <DevHubAlert severity="error">{error}</DevHubAlert>
          </DevHubBox>
        )}
      </DevHubDialogContent>
      <DevHubDialogActions>
        <DevHubButton
          variant="outlined"
          color="secondary"
          height="large"
          onClick={handleClose}
        >
          Cancel
        </DevHubButton>
        <DevHubButton
          variant="contained"
          color="primary"
          height="large"
          onClick={handleAdd}
          disabled={saving}
          startIcon={
            saving ? (
              <DevHubCircularProgress size={14} />
            ) : (
              <AddIcon fontSize="small" />
            )
          }
        >
          {saving ? 'Saving…' : 'Add Server'}
        </DevHubButton>
      </DevHubDialogActions>
    </DevHubDialog>
  );
};

interface FlowPoolPickerDialogProps {
  open: boolean;
  onClose: () => void;
  flowPools: FlowPool[];
  flowPoolsLoading: boolean;
  flowPoolsError: string | null;
  selectedPool: FlowPool | null;
  setSelectedPool: (pool: FlowPool | null) => void;
  flowSaving: boolean;
  channelEntries: ContextEntry[];
  onLink: (credentials?: FlowEntryCredentials) => void;
}

/**
 * FlowPoolPickerDialog
 * @param opts options
 * @constructor
 */
export const FlowPoolPickerDialog = ({
  open,
  onClose,
  flowPools,
  flowPoolsLoading,
  flowPoolsError,
  selectedPool,
  setSelectedPool,
  flowSaving,
  channelEntries,
  onLink,
}: FlowPoolPickerDialogProps) => {
  const [manualOrg, setManualOrg] = useState('');
  const [manualPool, setManualPool] = useState('');
  const [credExpanded, setCredExpanded] = useState(false);
  const [creds, setCreds] = useState<EntryCredentialsState>(
    EMPTY_ENTRY_CREDENTIALS,
  );

  const handleManualChange = (org: string, poolId: string) => {
    if (org.trim() && poolId.trim()) {
      setSelectedPool({
        org: org.trim(),
        poolId: poolId.trim(),
        description: '',
      });
    } else {
      setSelectedPool(null);
    }
  };

  const resetCreds = () => {
    setCredExpanded(false);
    setCreds(EMPTY_ENTRY_CREDENTIALS);
  };

  const handleClose = () => {
    setManualOrg('');
    setManualPool('');
    resetCreds();
    onClose();
  };

  const handleLink = () => {
    const hasCredentials =
      creds.tokenUrl.trim() &&
      creds.clientId.trim() &&
      creds.clientSecret.trim();
    onLink(hasCredentials ? creds : undefined);
    resetCreds();
  };

  let body: ReactNode;
  if (flowPoolsLoading) {
    body = (
      <DevHubBox display="flex" justifyContent="center" p={3}>
        <DevHubCircularProgress size={28} />
      </DevHubBox>
    );
  } else if (flowPoolsError) {
    body = <DevHubAlert severity="error">{flowPoolsError}</DevHubAlert>;
  } else if (flowPools.length === 0) {
    body = null;
  } else {
    body = (
      <DevHubBox display="flex" flexDirection="column" style={{ gap: 8 }}>
        {flowPools.map(pool => {
          const isSelected =
            selectedPool?.org === pool.org &&
            selectedPool?.poolId === pool.poolId;
          const alreadyLinked = channelEntries.some(
            e =>
              e.type === 'flow' &&
              e.org === pool.org &&
              e.poolId === pool.poolId,
          );
          let poolBg = '#fff';
          if (isSelected) poolBg = '#e3f2fd';
          else if (alreadyLinked) poolBg = '#f5f5f5';
          return (
            <DevHubBox
              key={`${pool.org}/${pool.poolId}`}
              display="flex"
              alignItems="center"
              style={{
                gap: 12,
                padding: '10px 12px',
                borderRadius: 6,
                cursor: alreadyLinked ? 'default' : 'pointer',
                border: `1px solid ${isSelected ? '#1976d2' : '#e0e0e0'}`,
                background: poolBg,
                opacity: alreadyLinked ? 0.6 : 1,
              }}
              onClick={() => !alreadyLinked && setSelectedPool(pool)}
            >
              <DevHubBox flex={1}>
                <DevHubTypography variant="body2" style={{ fontWeight: 600 }}>
                  {pool.poolId}
                </DevHubTypography>
                <DevHubTypography variant="caption" style={{ opacity: 0.6 }}>
                  {pool.org}
                  {pool.description ? ` — ${pool.description}` : ''}
                </DevHubTypography>
              </DevHubBox>
              {alreadyLinked && (
                <DevHubChip
                  label="Linked"
                  chipColor="success"
                  chipSize="small"
                  noMargins
                />
              )}
            </DevHubBox>
          );
        })}
      </DevHubBox>
    );
  }

  const manualSection = (
    <DevHubBox
      display="flex"
      flexDirection="column"
      style={{ gap: 12, marginTop: flowPools.length > 0 ? 16 : 0 }}
    >
      {flowPools.length > 0 && (
        <DevHubBox display="flex" alignItems="center" style={{ gap: 8 }}>
          <DevHubDivider style={{ flex: 1 }} />
          <DevHubTypography
            variant="caption"
            style={{ opacity: 0.5, whiteSpace: 'nowrap' }}
          >
            or enter manually
          </DevHubTypography>
          <DevHubDivider style={{ flex: 1 }} />
        </DevHubBox>
      )}
      <DevHubTextField
        label="Org"
        fullWidth
        value={manualOrg}
        onChange={(e: any) => {
          setManualOrg(e.target.value);
          handleManualChange(e.target.value, manualPool);
        }}
        placeholder="my-org"
        {...FIELD_STYLE}
      />
      <DevHubTextField
        label="Pool ID"
        fullWidth
        value={manualPool}
        onChange={(e: any) => {
          setManualPool(e.target.value);
          handleManualChange(manualOrg, e.target.value);
        }}
        placeholder="my-pool"
        {...FIELD_STYLE}
      />
      <DevHubBox style={{ marginTop: 4 }}>
        <DevHubButton
          variant="text"
          size="small"
          onClick={() => setCredExpanded(v => !v)}
        >
          {credExpanded
            ? '▾ Hide credentials'
            : '▸ Add custom credentials (optional)'}
        </DevHubButton>
        {credExpanded && (
          <DevHubBox style={{ marginTop: 8 }}>
            <EntryCredentialsFields
              value={creds}
              onChange={setCreds}
              caption="These credentials will be used when Nova calls this FLOW pool. If omitted, the channel or server credentials are used."
            />
          </DevHubBox>
        )}
      </DevHubBox>
    </DevHubBox>
  );

  return (
    <DevHubDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DevHubDialogTitle>Link FLOW:Context Pool</DevHubDialogTitle>
      <DevHubDialogContent>
        <DevHubDivider style={{ margin: '4px 0 16px 0' }} />
        {body}
        {!flowPoolsLoading && !flowPoolsError && manualSection}
      </DevHubDialogContent>
      <DevHubDialogActions>
        <DevHubButton
          variant="outlined"
          color="secondary"
          height="large"
          onClick={handleClose}
        >
          Cancel
        </DevHubButton>
        <DevHubButton
          variant="contained"
          color="primary"
          height="large"
          onClick={handleLink}
          disabled={!selectedPool || flowSaving}
          startIcon={
            flowSaving ? (
              <DevHubCircularProgress size={14} />
            ) : (
              <AddIcon fontSize="small" />
            )
          }
        >
          {flowSaving ? 'Saving…' : 'Link Pool'}
        </DevHubButton>
      </DevHubDialogActions>
    </DevHubDialog>
  );
};
